import type { BankAccountKind, BankConnection, ParsedResidenceSheet, Person, PersonInput, ResidenceInput, Tag, TagKind } from '@tally/shared';

const API_BASE = import.meta.env.VITE_API_URL || '/api';

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const response = await fetch(`${API_BASE}${path}`, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...options.headers },
    ...options,
  });

  if (response.status === 401) {
    const refreshed = await refreshToken();
    if (refreshed) {
      const retry = await fetch(`${API_BASE}${path}`, {
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...options.headers },
        ...options,
      });
      if (retry.ok) return retry.json();
    }
    const publicPaths = ['/login', '/'];
    if (!publicPaths.includes(window.location.pathname)) {
      window.location.href = '/login';
    }
    throw new Error('Session expired');
  }

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: { message: 'Request failed' } }));
    throw new Error(error.error?.message || `HTTP ${response.status}`);
  }

  return response.json();
}

async function refreshToken(): Promise<boolean> {
  try {
    const res = await fetch(`${API_BASE}/auth/refresh`, { method: 'POST', credentials: 'include' });
    return res.ok;
  } catch {
    return false;
  }
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown) => request<T>(path, { method: 'POST', body: body ? JSON.stringify(body) : undefined }),
  patch: <T>(path: string, body: unknown) => request<T>(path, { method: 'PATCH', body: JSON.stringify(body) }),
  put: <T>(path: string, body: unknown) => request<T>(path, { method: 'PUT', body: JSON.stringify(body) }),
  delete: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
};

export const authApi = {
  getAuthUrl: (provider: string) => api.get<{ success: true; data: { url: string } }>(`/auth/${provider}`),
  getGoogleAuthUrl: () => api.get<{ success: true; data: { url: string } }>('/auth/google'),
  emailRegister: (email: string, password: string, name: string) => api.post<{ success: true; data: any }>('/auth/email/register', { email, password, name }),
  getMe: () => api.get<{ success: true; data: any }>('/auth/me'),
  logout: () => api.post('/auth/logout'),
  /** Irreversible: deletes the account and all its data. */
  deleteAccount: () => request<{ success: true; data: null }>('/auth/me', { method: 'DELETE', body: JSON.stringify({ confirm: 'DELETE' }) }),
};

export const accountsApi = {
  list: () => api.get<{ success: true; data: any[] }>('/accounts'),
  getEmailAuthUrl: () => api.get<{ success: true; data: { url: string } }>('/accounts/email/google/auth-url'),
  remove: (id: string) => api.delete(`/accounts/${id}`),
  sync: (id: string) => api.post(`/accounts/${id}/sync`),
  scan: (id: string) => api.get<{ success: true; data: ScanActivityData }>(`/accounts/${id}/scan`),
  pauseScan: (id: string) => api.post(`/accounts/${id}/scan/pause`),
  resumeScan: (id: string, body: { mode: 'all' | 'sample'; count?: number }) => api.post(`/accounts/${id}/scan/resume`, body),
  syncStatus: () => api.get<{ success: true; data: { accounts: any[]; overall: { totalAccounts: number; pendingJobs: number; activeJobs: number; hasErrors: boolean; isSyncing: boolean } } }>('/accounts/sync-status'),
};

export const tripsApi = {
  list: (params?: Record<string, string>) => {
    const qs = params ? '?' + new URLSearchParams(params).toString() : '';
    return api.get<{ success: true; data: { items: any[]; total: number; hasMore: boolean } }>(`/trips${qs}`);
  },
  get: (id: string) => api.get<{ success: true; data: any }>(`/trips/${id}`),
  update: (id: string, body: any) => api.patch(`/trips/${id}`, body),
};

export const transactionsApi = {
  list: (params?: Record<string, string>) => {
    const qs = params ? '?' + new URLSearchParams(params).toString() : '';
    return api.get<{ success: true; data: { items: any[]; total: number; hasMore: boolean } }>(`/transactions${qs}`);
  },
  summary: (params?: Record<string, string>) => {
    const qs = params ? '?' + new URLSearchParams(params).toString() : '';
    return api.get<{ success: true; data: any }>(`/transactions/summary${qs}`);
  },
  update: (id: string, body: any) => api.patch(`/transactions/${id}`, body),
  setNotSpending: (id: string, body: { notSpending: boolean; wholeMerchant?: boolean }) =>
    api.post<{ success: true; data: { id: string } }>(`/transactions/${id}/not-spending`, body),
  bulk: (body: { transactionIds: string[]; tripId?: string | null; addTagIds?: string[]; removeTagIds?: string[] }) =>
    api.post<{ success: true; data: { updated: number; moved: number } }>('/transactions/bulk', body),
  delete: (id: string) => api.delete<{ success: true; data: null }>(`/transactions/${id}`),
  merge: (transactionIds: string[]) => api.post<{ success: true; data: { keptTransactionId: string; deletedTransactionIds: string[]; matchGroupId: string } }>('/transactions/merge', { transactionIds }),
  emails: (id: string) => api.get<{ success: true; data: Array<{ messageId: string; subject: string; from: string; to: string; date: string; body: string }> }>(`/transactions/${id}/emails`),
  export: (params: Record<string, string>) => {
    const qs = new URLSearchParams(params).toString();
    return `${API_BASE}/transactions/export?${qs}`;
  },
};

export const tagsApi = {
  list: () => api.get<{ success: true; data: Tag[] }>('/tags'),
  /** Returns the existing tag when one with the same name (any case) exists. */
  create: (body: { name: string; kind?: TagKind; color?: string | null }) =>
    api.post<{ success: true; data: { id: string; name: string; created: boolean } }>('/tags', body),
  update: (id: string, body: { name?: string; kind?: TagKind; color?: string | null }) => api.patch(`/tags/${id}`, body),
  remove: (id: string) => api.delete<{ success: true; data: null }>(`/tags/${id}`),
  suggestions: (transactionIds: string[]) =>
    api.get<{ success: true; data: TagSuggestion[] }>(`/tags/suggestions?transactionIds=${encodeURIComponent(transactionIds.join(','))}`),
  findMatches: (id: string) =>
    api.post<{ success: true; data: Array<{ merchantKey: string; name: string; confidence: number; transactionIds: string[] }> }>(`/tags/${id}/find-matches`),
  runAi: () => api.post<{ success: true; data: { asked: number; pending: number; tagsApplied: number } }>('/tags/ai/run'),
};

export interface TagSuggestion {
  name: string;
  tagId: string | null;
  confidence: number;
}

export interface SubscriptionItem {
  key: string;
  merchant: string;
  cadence: 'weekly' | 'monthly' | 'quarterly' | 'yearly';
  transactionIds: string[];
  count: number;
  firstDate: string;
  lastDate: string;
  typicalAmount: number;
  currency: string;
  typicalUsd: number | null;
  totalUsd: number;
  monthlyUsd: number | null;
  yearlyUsd: number | null;
  nextExpected: string;
  active: boolean;
  confidence: number;
  tagId: string | null;
}

export const subscriptionsApi = {
  list: () => api.get<{ success: true; data: { items: SubscriptionItem[]; summary: { activeCount: number; monthlyUsd: number; yearlyUsd: number; totalUsd: number } } }>('/subscriptions'),
  refresh: () => api.post<{ success: true; data: { subscriptions: number; tagsCreated: number } }>('/subscriptions/refresh'),
};

export interface PeriodTotal { key: '3m' | '1y' | '2y' | '3y' | 'all'; label: string; from: string | null; totalUsd: number; count: number }
export interface MonthTotal { month: string; totalUsd: number; tripUsd: number; otherUsd: number }
export interface MerchantTotal { key: string; name: string; totalUsd: number; count: number; avgUsd: number; first: string; last: string }

export interface InsightsData {
  from: string | null;
  to: string | null;
  totalUsd: number;
  count: number;
  months: number;
  avgPerMonthUsd: number;
  byMonth: MonthTotal[];
  merchants: MerchantTotal[];
  categories: Array<{ category: string; totalUsd: number; count: number }>;
  tags: Array<{ id: string; name: string; color: string | null; totalUsd: number; count: number }>;
  periods: PeriodTotal[];
}

export interface MerchantDetailData extends MerchantTotal {
  periods: PeriodTotal[];
  byYear: Array<{ year: string; totalUsd: number; count: number }>;
  byMonth: MonthTotal[];
  subscriptions: SubscriptionItem[];
}

export const insightsApi = {
  get: (range: { from?: string; to?: string }) => {
    const params = new URLSearchParams(Object.entries(range).filter(([, v]) => !!v) as Array<[string, string]>).toString();
    return api.get<{ success: true; data: InsightsData }>(`/insights${params ? `?${params}` : ''}`);
  },
  merchant: (key: string) => api.get<{ success: true; data: MerchantDetailData }>(`/insights/merchants/${encodeURIComponent(key)}`),
};

export interface StorageReport {
  usedBytes: number;
  limitBytes: number;
  archives: Array<{ table: string; rows: number; bytes: number }>;
  archiveExpiresOn: string;
}

export const dashboardApi = {
  summary: () => api.get<{ success: true; data: any }>('/dashboard/summary'),
  storage: () => api.get<{ success: true; data: StorageReport }>('/dashboard/storage'),
};

export interface ResidenceImportResult extends ParsedResidenceSheet {
  imported: { peopleCreated: string[]; peopleUpdated: string[]; residencesImported: number } | null;
}

export const peopleApi = {
  list: () => api.get<{ success: true; data: Person[] }>('/people'),
  create: (body: PersonInput) => api.post<{ success: true; data: { id: string } }>('/people', body),
  update: (id: string, body: Partial<PersonInput>) => api.patch<{ success: true; data: { id: string } }>(`/people/${id}`, body),
  remove: (id: string) => api.delete<{ success: true; data: null }>(`/people/${id}`),
  replaceResidences: (id: string, residences: ResidenceInput[]) =>
    api.put<{ success: true; data: { count: number } }>(`/people/${id}/residences`, { residences }),
  importAi: (body: { text?: string; image?: { mimeType: string; data: string } }) =>
    api.post<{ success: true; data: ResidenceImportResult & { text: string; notes: string[]; model: string } }>('/people/import/ai', body),
  importSheet: (text: string, dryRun: boolean) =>
    api.post<{ success: true; data: ResidenceImportResult }>('/people/import', { text, dryRun }),
};

export interface ScanActivityData {
  state: 'sampling' | 'running' | 'paused' | 'awaiting_review';
  sampleSize: number;
  stages: { listed: number; candidate: number; skipped: number; extracted: number; failed: number };
  byKind: Array<{ kind: string; count: number; verified: number }>;
  lastRun: null | {
    finishedAt: string; durationMs: number; extracted: number; quotaExhausted: boolean; llmUnavailable: boolean;
    usage?: { inputTokens: number; outputTokens: number };
  };
  jobs: Array<{ status: string; attempts: number; lastError: string | null; progress: number; updatedAt: string }>;
  recent: Array<{ messageId: string; emailDate: string | null; fromDomain: string | null; subject: string | null; model: string; promptVersion: string; deepLink: string; extraction: any }>;
  failures: Array<{ messageId: string; subject: string | null; reason: string | null; attempts: number }>;
}

export const banksApi = {
  list: () => api.get<{ success: true; data: BankConnection[] }>('/banks'),
  connectSimplefin: (setupToken: string) => api.post<{ success: true; data: { id: string } }>('/banks/simplefin', { setupToken }),
  sync: (id: string) => api.post<{ success: true; data: { started: boolean } }>(`/banks/${id}/sync`),
  updateAccount: (id: string, body: { kind?: BankAccountKind; hidden?: boolean }) => api.patch<{ success: true }>(`/banks/accounts/${id}`, body),
  remove: (id: string) => api.delete<{ success: true }>(`/banks/${id}`),
};
